import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConversationTimeline } from '../components/deep-analysis';
import {
  ConversationTimelineItem,
  generateTimelineId,
} from '../types/conversation';

interface SharedAnalysisData {
  shareToken: string;
  notes: string | null;
  viewCount: number;
  includeFollowups: boolean;
  sharedBy: string;
  sharedAt: string;
  session: {
    id: string;
    status: string;
    documentText: string;
    finalReport: string | null;
    tokensUsed: {
      input: number;
      output: number;
      total: number;
    };
    createdAt: string;
    completedAt: string | null;
    clarificationRequests: Array<{
      id: string;
      requestId: string;
      questions: Array<{
        question: string;
        options: Array<{ label: string; description: string }>;
        allowOther: boolean;
      }>;
      responses: Record<string, string> | null;
      status: 'pending' | 'responded' | 'expired';
      createdAt: string;
    }>;
    conversationMessages: Array<{
      id: string;
      role: 'user' | 'assistant';
      content: string;
      referencedJudgments: string[];
      tokensUsed: number;
      createdAt: string;
    }>;
  };
}

export default function SharedAnalysisPage() {
  const { t, i18n } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<SharedAnalysisData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSharedAnalysis = async () => {
      if (!token) {
        setError(t('shared.noToken'));
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/v1/public/analysis/${token}`);

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(t('shared.analysisNotFound'));
          }
          throw new Error(t('shared.loadAnalysisError'));
        }

        const result = await response.json();
        setData(result);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSharedAnalysis();
  }, [token]);

  // Convert session data to timeline format for ConversationTimeline
  const timeline = useMemo((): ConversationTimelineItem[] => {
    if (!data) return [];

    const items: ConversationTimelineItem[] = [];
    let roundNumber = 1;

    // Add document text as user input
    items.push({
      id: generateTimelineId(),
      type: 'user_input',
      content: data.session.documentText,
      createdAt: data.session.createdAt,
      roundNumber,
    });

    // Add clarification requests and their responses
    for (const clarification of data.session.clarificationRequests) {
      // Build responses array for the ClarificationRequestItem
      let responses: Array<{ questionIndex: number; selectedOption?: string; customResponse?: string }> | undefined;

      if (clarification.status === 'responded' && clarification.responses) {
        responses = clarification.questions.map((_, idx) => {
          const responseKey = Object.keys(clarification.responses || {})[idx];
          const responseValue = clarification.responses?.[responseKey];
          return {
            questionIndex: idx,
            customResponse: responseValue || undefined,
            selectedOption: undefined,
          };
        });
      }

      items.push({
        id: clarification.id,
        type: 'clarification_request',
        requestId: clarification.requestId,
        questions: clarification.questions.map((q) => ({
          question: q.question,
          options: q.options,
          allowOther: q.allowOther,
        })),
        status: clarification.status === 'responded' ? 'answered' : 'pending',
        responses,
        createdAt: clarification.createdAt,
        roundNumber,
      });

      if (clarification.status === 'responded') {
        roundNumber++;
      }
    }

    // Add final report
    if (data.session.finalReport) {
      items.push({
        id: generateTimelineId(),
        type: 'report',
        markdown: data.session.finalReport,
        createdAt: data.session.completedAt || data.session.createdAt,
        roundNumber,
      });
    }

    // Add follow-up conversation if included
    if (data.includeFollowups && data.session.conversationMessages) {
      for (const message of data.session.conversationMessages) {
        if (message.role === 'user') {
          items.push({
            id: message.id,
            type: 'followup_question',
            content: message.content,
            createdAt: message.createdAt,
            roundNumber,
          });
        } else {
          items.push({
            id: message.id,
            type: 'followup_response',
            content: message.content,
            referencedJudgments: message.referencedJudgments,
            tokensUsed: message.tokensUsed,
            isStreaming: false,
            createdAt: message.createdAt,
            roundNumber,
          });
        }
      }
    }

    return items;
  }, [data]);

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'pl-PL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <svg
            className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <p className="text-slate-600 dark:text-slate-400">{t('shared.loadingAnalysis')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-500 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            {t('shared.notFound')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            {t('shared.backToHome')}
          </Link>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 dark:text-white">{t('brand.name')}</h1>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('shared.sharedAnalysis')}</p>
              </div>
            </Link>
            <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                <span>{t('shared.views', { count: data.viewCount })}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        {/* Meta info card */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 mb-4">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>{t('shared.sharedBy')} <strong className="text-slate-900 dark:text-white">{data.sharedBy}</strong></span>
            </div>
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{formatDate(data.sharedAt)}</span>
            </div>
            {data.session.tokensUsed.total > 0 && (
              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>{data.session.tokensUsed.total.toLocaleString()} {t('shared.tokens')}</span>
              </div>
            )}
          </div>

          {/* Shared notes */}
          {data.notes && (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                <div>
                  <span className="text-xs font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wider">{t('shared.noteLabel')}</span>
                  <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">{data.notes}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Read-only badge */}
        <div className="mb-4 flex items-center justify-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-full text-sm text-slate-600 dark:text-slate-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            {t('shared.readOnly')}
          </div>
        </div>

        {/* Timeline container */}
        <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col min-h-[500px]">
          <ConversationTimeline
            timeline={timeline}
            readOnly={true}
          />
        </div>
      </main>

      {/* CTA */}
      <div className="bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            {t('shared.ownAnalysisCta')}
          </p>
          <Link
            to="/analysis"
            className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            {t('shared.startAnalysis')}
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-slate-500 dark:text-slate-400">
            {t('footer.text')}
          </p>
        </div>
      </footer>
    </div>
  );
}
